/**
 * PoamEditorDialog — Add / Edit a Plan of Action & Milestones (POA&M) item
 *
 * POA&M items are the remediation backlog for a framework. Federal frameworks
 * (CMMC, FedRAMP) require a 180-day maximum closure window. Others use it as
 * a general improvement plan.
 *
 * Fields:
 *  - Title, description
 *  - Owner (assigned user)
 *  - Status (open / in_progress / completed)
 *  - Open date, target close date
 *  - Milestones (ordered list with status each)
 *  - Linked control ID (optional)
 *
 * Dialog works in both "add" and "edit" modes based on whether `item` is null.
 */

import { useState, useEffect } from 'react';
import { useTrustEntities } from '@/lib/use-trust-entities';
import {
  Flag, Plus, X, Calendar, User as UserIcon, CheckCircle2,
  AlertTriangle, ListChecks, GripVertical,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

export interface PoamEditorItem {
  id: string;
  name: string;
  description: string;
  status: 'open' | 'in_progress' | 'completed';
  owner: string;
  openDate: string;
  targetCloseDate: string;
  milestones: string[];
  linkedControlId?: string | null;
}

// Hardcoded owners list removed — POA&M owner is now selected from real
// tenant users via the /users API (see useTrustEntities() in component).

interface PoamEditorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: PoamEditorItem | null;
  federal: boolean;
  controls: Array<{ id: string; name: string }>;
  onSave: (item: PoamEditorItem) => void;
  onDelete?: (id: string) => void;
}

const MILESTONE_STATES = ['pending', 'in progress', 'done', 'due'] as const;
type MilestoneStatus = typeof MILESTONE_STATES[number];

function parseMilestone(raw: string): { text: string; status: MilestoneStatus } {
  const match = raw.match(/\s*\((done|in progress|due)\)\s*$/i);
  if (!match) return { text: raw.trim(), status: 'pending' };
  const status = match[1].toLowerCase() as MilestoneStatus;
  return { text: raw.replace(match[0], '').trim(), status };
}

function formatMilestone(text: string, status: MilestoneStatus): string {
  if (status === 'pending') return text;
  return `${text} (${status})`;
}

export function PoamEditorDialog({
  open, onOpenChange, item, federal, controls, onSave, onDelete,
}: PoamEditorDialogProps) {
  const isEdit = item !== null;
  const entities = useTrustEntities();
  const OWNERS = entities.users.map(u => `${u.name}${u.role && u.role !== '—' ? ` (${u.role})` : ''}`);
  const title = isEdit ? 'Edit POA&M Item' : 'New POA&M Item';

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<'open' | 'in_progress' | 'completed'>('open');
  const [owner, setOwner] = useState('');
  const [openDate, setOpenDate] = useState('');
  const [targetCloseDate, setTargetCloseDate] = useState('');
  const [milestones, setMilestones] = useState<Array<{ text: string; status: MilestoneStatus }>>([]);
  const [newMilestone, setNewMilestone] = useState('');
  const [linkedControlId, setLinkedControlId] = useState<string>('none');

  useEffect(() => {
    if (!open) return;
    if (item) {
      setName(item.name);
      setDescription(item.description);
      setStatus(item.status);
      setOwner(item.owner);
      setOpenDate(item.openDate.slice(0, 10));
      setTargetCloseDate(item.targetCloseDate.slice(0, 10));
      setMilestones(item.milestones.map(parseMilestone));
      setLinkedControlId(item.linkedControlId ?? 'none');
    } else {
      // Defaults for new item
      const today = new Date().toISOString().slice(0, 10);
      const defaultTarget = new Date(Date.now() + (federal ? 180 : 90) * 24 * 60 * 60 * 1000)
        .toISOString().slice(0, 10);
      setName('');
      setDescription('');
      setStatus('open');
      setOwner('');
      setOpenDate(today);
      setTargetCloseDate(defaultTarget);
      setMilestones([]);
      setNewMilestone('');
      setLinkedControlId('none');
    }
  }, [item, open, federal]);

  const addMilestone = () => {
    const trimmed = newMilestone.trim();
    if (!trimmed) return;
    setMilestones(prev => [...prev, { text: trimmed, status: 'pending' }]);
    setNewMilestone('');
  };

  const removeMilestone = (idx: number) => {
    setMilestones(prev => prev.filter((_, i) => i !== idx));
  };

  const cycleMilestoneStatus = (idx: number) => {
    setMilestones(prev => prev.map((m, i) => {
      if (i !== idx) return m;
      const nextIdx = (MILESTONE_STATES.indexOf(m.status) + 1) % MILESTONE_STATES.length;
      return { ...m, status: MILESTONE_STATES[nextIdx] };
    }));
  };

  const moveMilestone = (idx: number, direction: 'up' | 'down') => {
    setMilestones(prev => {
      const next = [...prev];
      const target = direction === 'up' ? idx - 1 : idx + 1;
      if (target < 0 || target >= next.length) return prev;
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  };

  const canSave = name.trim().length >= 3 && description.trim().length >= 10 && owner && openDate && targetCloseDate;

  // Federal 180-day rule validation
  const closureWindow = (() => {
    if (!openDate || !targetCloseDate) return null;
    const days = Math.ceil((new Date(targetCloseDate).getTime() - new Date(openDate).getTime()) / (1000 * 60 * 60 * 24));
    return days;
  })();
  const federalViolation = federal && closureWindow !== null && closureWindow > 180;

  const handleSave = () => {
    if (!canSave) {
      toast.error('Fill out required fields');
      return;
    }
    if (federalViolation) {
      toast.error('Federal frameworks require ≤180-day closure window', {
        description: `Current window is ${closureWindow} days. Reduce the target close date.`,
      });
      return;
    }
    const payload: PoamEditorItem = {
      id: item?.id ?? `poam-${Date.now()}`,
      name: name.trim(),
      description: description.trim(),
      status,
      owner,
      openDate: new Date(openDate).toISOString(),
      targetCloseDate: new Date(targetCloseDate).toISOString(),
      milestones: milestones.map(m => formatMilestone(m.text, m.status)),
      linkedControlId: linkedControlId === 'none' ? null : linkedControlId,
    };
    onSave(payload);
    toast.success(isEdit ? 'POA&M item updated' : 'POA&M item added');
    onOpenChange(false);
  };

  const handleDelete = () => {
    if (!item || !onDelete) return;
    if (!confirm(`Delete "${item.name}"? This removes the item from the remediation plan.`)) return;
    onDelete(item.id);
    toast.success('POA&M item deleted');
    onOpenChange(false);
  };

  const milestoneStatusStyle: Record<MilestoneStatus, string> = {
    pending: 'bg-muted text-muted-foreground border-border',
    'in progress': 'bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/30',
    done: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30',
    due: 'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30',
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-hidden flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b">
          <DialogTitle className="flex items-center gap-2">
            <Flag className="h-5 w-5 text-primary" />
            {title}
          </DialogTitle>
          <DialogDescription>
            {federal
              ? 'Federal POA&M items must be closed within 180 days per CMMC/FedRAMP rules.'
              : 'Remediation item for this framework. Track milestones to closure.'}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto min-h-0">
          <div className="px-6 py-5 space-y-5">
            <div className="space-y-2">
              <Label htmlFor="poam-name" className="text-xs font-semibold">
                Title <span className="text-red-500">*</span>
              </Label>
              <Input
                id="poam-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Implement MFA for all privileged accounts"
                className="h-9 text-xs"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="poam-desc" className="text-xs font-semibold">
                Description <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="poam-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What gap does this address, and what's the remediation approach?"
                rows={3}
                className="text-xs"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-xs font-semibold">Status</Label>
                <Select value={status} onValueChange={(v) => setStatus(v as typeof status)}>
                  <SelectTrigger className="h-9 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open" className="text-xs">Open</SelectItem>
                    <SelectItem value="in_progress" className="text-xs">In Progress</SelectItem>
                    <SelectItem value="completed" className="text-xs">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="poam-owner" className="text-xs font-semibold">
                  Owner <span className="text-red-500">*</span>
                </Label>
                <Select value={owner} onValueChange={setOwner}>
                  <SelectTrigger id="poam-owner" className="h-9 text-xs">
                    <SelectValue placeholder="Select owner..." />
                  </SelectTrigger>
                  <SelectContent>
                    {OWNERS.length === 0 && (
                      <div className="px-2 py-1.5 text-[11px] text-muted-foreground">No users available</div>
                    )}
                    {OWNERS.map(o => (
                      <SelectItem key={o} value={o} className="text-xs">{o}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="poam-open" className="text-xs font-semibold">Open date</Label>
                <Input
                  id="poam-open"
                  type="date"
                  value={openDate}
                  onChange={(e) => setOpenDate(e.target.value)}
                  className="h-9 text-xs"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="poam-target" className="text-xs font-semibold">Target close date</Label>
                <Input
                  id="poam-target"
                  type="date"
                  value={targetCloseDate}
                  onChange={(e) => setTargetCloseDate(e.target.value)}
                  className="h-9 text-xs"
                  min={openDate}
                />
              </div>
            </div>

            {closureWindow !== null && (
              <div className={cn(
                'text-[11px] px-3 py-2 rounded-md border flex items-center gap-2',
                federalViolation
                  ? 'bg-red-50 dark:bg-red-950/30 border-red-300 dark:border-red-900 text-red-800 dark:text-red-300'
                  : 'bg-muted/30 text-muted-foreground',
              )}>
                <Calendar className="h-3.5 w-3.5" />
                Closure window: <strong>{closureWindow} days</strong>
                {federal && (
                  <>
                    <span>·</span>
                    {federalViolation ? (
                      <span className="font-semibold">Exceeds 180-day federal limit — reduce target date.</span>
                    ) : (
                      <span className="text-emerald-700 dark:text-emerald-400">Within 180-day federal limit.</span>
                    )}
                  </>
                )}
              </div>
            )}

            {controls.length > 0 && (
              <div className="space-y-2">
                <Label className="text-xs font-semibold">
                  Linked control <span className="text-muted-foreground font-normal">(optional)</span>
                </Label>
                <Select value={linkedControlId} onValueChange={setLinkedControlId}>
                  <SelectTrigger className="h-9 text-xs">
                    <SelectValue placeholder="No linked control" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="none" className="text-xs">— No linked control —</SelectItem>
                    {controls.map(c => (
                      <SelectItem key={c.id} value={c.id} className="text-xs">
                        <span className="font-mono">{c.id}</span> — {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <Separator />

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-semibold flex items-center gap-1.5">
                  <ListChecks className="h-3.5 w-3.5" />
                  Milestones
                </Label>
                <span className="text-[10px] text-muted-foreground">Click status badge to cycle</span>
              </div>

              {milestones.length === 0 ? (
                <p className="text-[11px] text-muted-foreground italic py-2">
                  No milestones yet. Add steps below to break down this item.
                </p>
              ) : (
                <div className="space-y-1.5">
                  {milestones.map((ms, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-2 rounded-md border p-2 bg-muted/20"
                    >
                      <div className="flex flex-col gap-0.5">
                        <button
                          type="button"
                          disabled={idx === 0}
                          onClick={() => moveMilestone(idx, 'up')}
                          className="text-muted-foreground hover:text-foreground disabled:opacity-20 text-[10px]"
                          aria-label="Move milestone up"
                        >▲</button>
                        <button
                          type="button"
                          disabled={idx === milestones.length - 1}
                          onClick={() => moveMilestone(idx, 'down')}
                          className="text-muted-foreground hover:text-foreground disabled:opacity-20 text-[10px]"
                          aria-label="Move milestone down"
                        >▼</button>
                      </div>
                      <button
                        type="button"
                        onClick={() => cycleMilestoneStatus(idx)}
                        className={cn(
                          'px-2 py-0.5 rounded border text-[10px] font-medium capitalize shrink-0 hover:opacity-80',
                          milestoneStatusStyle[ms.status],
                        )}
                      >
                        {ms.status}
                      </button>
                      <span className={cn(
                        'text-xs flex-1 min-w-0',
                        ms.status === 'done' && 'line-through text-muted-foreground',
                      )}>
                        {ms.text}
                      </span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 shrink-0"
                        onClick={() => removeMilestone(idx)}
                        aria-label="Remove milestone"
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex gap-2">
                <Input
                  value={newMilestone}
                  onChange={(e) => setNewMilestone(e.target.value)}
                  placeholder="Add a milestone..."
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); addMilestone(); }
                  }}
                  className="h-8 text-xs"
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={addMilestone}
                  disabled={!newMilestone.trim()}
                  className="h-8"
                >
                  <Plus className="h-3 w-3 mr-1" /> Add
                </Button>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="px-6 py-4 border-t bg-muted/20">
          <div className="flex items-center justify-between w-full">
            <div>
              {isEdit && onDelete && (
                <Button variant="ghost" size="sm" onClick={handleDelete} className="text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30">
                  Delete item
                </Button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={!canSave || federalViolation}>
                <CheckCircle2 className="h-4 w-4 mr-1" />
                {isEdit ? 'Save changes' : 'Add item'}
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
